import { useCallback, useMemo, useRef } from "react";
import { NetworkStatus, type TypedDocumentNode } from "@apollo/client";
import { useQuery } from "@apollo/client/react";

import type {
  AccessSummaryMember,
  WorkspaceRosterSource,
} from "./use-object-sharing";
import { DEFAULT_PAGE_SIZE } from "#app/lib/constants/page-size";
import type { LiveRefetchContext } from "#app/lib/subscriptions/live-workspace-sync";

type RosterVariables = {
  issueId?: string;
  workflowId?: string;
  entityViewId?: string;
  skip: number;
  limit: number;
  teamSkip: number;
  teamLimit: number;
  search: string | null;
  grantedOnly: boolean;
};

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

// The people who reach the subject through the workspace alone — the rows
// the share dialog lists under its workspace row. The summary query with
// `grantedOnly` off returns every live member resolved; the roster is the
// slice reached through a public-access role and nothing else (a member
// with a share of their own sits in the "Shared with" group, and the owner
// and admins lead the band from the granted summary). Runs only while the
// row is open. Every share mutation refetches the summary operation by
// name, so a change made on any row refreshes this instance too.
export function useWorkspaceRoster(source: WorkspaceRosterSource, open: boolean) {
  const document = source.document as TypedDocumentNode<unknown, RosterVariables>;
  const variables = useMemo(
    (): RosterVariables => ({
      [source.idKey]: source.id ?? "",
      skip: 0,
      limit: PAGE_SIZE,
      teamSkip: 0,
      teamLimit: 0,
      search: null,
      grantedOnly: false,
    }),
    [source.idKey, source.id],
  );
  // A live change re-reads every member already loaded, not the first page
  // only. The ref holds the latest count; the context stays stable.
  const liveRefetchRef = useRef<() => Promise<unknown>>(() => Promise.resolve());
  const context = useMemo(
    (): LiveRefetchContext => ({ liveRefetch: () => liveRefetchRef.current() }),
    [],
  );
  const { data, loading, networkStatus, fetchMore, refetch } = useQuery(document, {
    variables,
    skip: !open || !source.id,
    notifyOnNetworkStatusChange: true,
    context,
  });
  const summary = useMemo(() => source.selectSummary(data), [source, data]);
  const members: AccessSummaryMember[] = useMemo(
    () =>
      (summary?.members ?? []).filter(
        (m) => !m.locked && m.enabled && m.source === "workspace_role",
      ),
    [summary],
  );
  const loaded = summary?.members.length ?? 0;
  const hasMore = loaded < (summary?.filteredMemberCount ?? 0);
  liveRefetchRef.current = () =>
    refetch({ ...variables, limit: Math.max(PAGE_SIZE, loaded) });
  const fetchingMore = networkStatus === NetworkStatus.fetchMore;
  const loadMore = useCallback(() => {
    if (!source.id || !hasMore || fetchingMore) return;
    void fetchMore({
      variables: { ...variables, skip: loaded },
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult
          ? (source.mergeMembers(prev, fetchMoreResult) as typeof prev)
          : prev,
    });
  }, [source, hasMore, fetchingMore, fetchMore, variables, loaded]);
  return {
    members,
    loading: loading && !summary,
    hasMore,
    loadMore,
  };
}
